import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import {
    FormFieldClickable,
    FormFieldIcon,
    FormFieldPresentationProps,
    FormFieldPropsMock,
} from "@insite/mobius/FormField";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import RecursivePartial from "@insite/mobius/utilities/RecursivePartial";
import { colorStrings, mediaQueriesBP } from "@solveindustrial/Utilities/variables";
import { css } from "styled-components";

export const commonTypoStyles = css`
    font-size: 16px;
    line-height: 30px;

    ${mediaQueriesBP.minMD} {
        font-size: 18px;
        line-height: 32px;
    }
`;

export const commonInputHeight = css`
    height: 30px;
    min-height: 30px;
    border-radius: 4px;

    ${mediaQueriesBP.minMD} {
        height: 50px;
        min-height: 50px;
    }
`;

export const commonInputIcons = css`
    ${commonInputHeight};
    width: 30px;
    top: 50%;
    transform: translateY(-50%);

    ${mediaQueriesBP.minMD} {
        width: 50px;
        height: ${({ _sizeVariant }: { _sizeVariant?: keyof typeof buttonSizeVariants }) => {
            const heightVariant = get(buttonSizeVariants, [_sizeVariant, "height"]) || 50;
            return `${heightVariant}px;`;
        }};
        min-height: ${({ _sizeVariant }: { _sizeVariant?: keyof typeof buttonSizeVariants }) => {
            const heightVariant = get(buttonSizeVariants, [_sizeVariant, "height"]) || 50;
            return `${heightVariant}px;`;
        }};
    }

    svg {
        color: ${getColor(colorStrings.primaryMain)};
        width: 20px;
        ${mediaQueriesBP.minMD} {
            width: 30px;
        }
    }
`;

export const formField: RecursivePartial<{
    defaultProps?: FormFieldPresentationProps<FormFieldPropsMock> | undefined;
}> = {
    defaultProps: {
        errorProps: {
            mergeCss: true,
            size: "12px",
            lineHeight: "14px",
            weight: 400,
        },
        cssOverrides: {
            formInputWrapper: css`
                input,
                select,
                select:not(.react-datetime-picker__inputGroup__input),
                .react-datetime-picker {
                    ${commonInputHeight}
                    ${commonTypoStyles}
                    border-radius: 4px !important; /* Avoid using !important */
                    border-color: ${getColor(colorStrings.commonGlobalGray03)} !important; /* Avoid using !important */
                    &:not(.react-datetime-picker) {
                        padding: 0 16px !important; /* Avoid using !important */
                    }

                    .react-datetime-picker__inputGroup__input {
                        /* Avoid using !important */
                        padding: 0 2px 0 !important;
                        &:focus {
                            /* Avoid using !important */
                            padding: 0 2px 0 !important;
                        }
                    }

                    &:not(.react-datetime-picker__inputGroup__input)[type="number"],
                    &:not(.react-datetime-picker__inputGroup__input)[type="number"]:focus {
                        /* Avoid using !important */
                        padding: 0 2px 0 8px !important;
                        ${mediaQueriesBP.minMD} {
                            padding: 0 2px 0 12px !important;
                        }
                    }

                    &:focus {
                        border-color: ${getColor(
                            colorStrings.commonPrimarySecond,
                        )} !important; /* Avoid using !important */
                        padding: 0 16px !important; /* Avoid using !important */
                    }

                    &:disabled {
                        color: ${getColor(colorStrings.commonGlobalGray03)};
                        background: ${getColor(colorStrings.commonGlobalWhite)} !important;
                        &::placeholder {
                            color: ${getColor(colorStrings.commonGlobalGray03)};
                        }

                        & + span {
                            background: transparent !important;

                            svg {
                                color: ${getColor(colorStrings.commonGlobalGray03)};
                            }
                        }

                        & + button {
                            svg {
                                stroke: ${getColor(colorStrings.commonGlobalGray03)};
                            }

                            span {
                                background: transparent !important; /* Avoid using !important */
                            }
                        }
                    }
                }

                select {
                    ${commonInputHeight}
                    ${commonTypoStyles}
                    border-radius: 4px !important; /* Avoid using !important */
                    border-color: ${getColor(colorStrings.commonGlobalGray03)} !important; /* Avoid using !important */
                    &:focus {
                        border-color: ${getColor(
                            colorStrings.commonPrimarySecond,
                        )} !important; /* Avoid using !important */
                        padding: 0 32px 0 10px !important; /* Avoid using !important */
                    }

                    &:disabled {
                        color: ${getColor(colorStrings.commonGlobalGray03)};
                    }
                }

                textarea {
                    border-radius: 4px !important; /* Avoid using !important */
                    &:focus {
                        border-color: ${getColor(
                            colorStrings.commonPrimarySecond,
                        )} !important; /* Avoid using !important */
                        padding: 10px 16px !important; /* Avoid using !important */
                    }
                    &:disabled {
                        color: ${getColor(colorStrings.commonGlobalGray03)};
                    }
                }

                ${FormFieldIcon} {
                    ${commonInputIcons};
                }

                ${FormFieldClickable} {
                    ${commonInputHeight};
                    width: 30px;

                    ${mediaQueriesBP.minMD} {
                        width: 50px;
                    }
                }
            `,
            inputSelect: css`
                ${commonInputHeight}
                ${commonTypoStyles}
                border-width: 1px !important; /* Avoid using !important */

                ${FormFieldIcon} {
                    ${commonInputIcons};
                }

                &:disabled {
                    background-color: ${getColor(colorStrings.commonGlobalWhite)};
                }
            `,
            formField: css`
                ${commonTypoStyles};

                input,
                input:not(.react-datetime-picker__inputGroup__input),
                select:not(.react-datetime-picker__inputGroup__input),
                .mobiusFileUpload {
                    ${commonTypoStyles};
                    ${commonInputHeight};
                }
            `,
        },
        labelProps: {
            css: css`
                ${commonTypoStyles}
                font-weight: 700;

                [disabled] & {
                    color: ${getColor(colorStrings.textDisabled)};
                }
            `,
        },
    },
};
